import type { ReactNode } from 'react';
import type { PermissionName } from '../../constants/permissions';
import { isPlatformRole, type UserRole } from '../../types/auth.types';

export type SidebarWorkspace = 'platform' | 'institution';
export type SidebarScope = SidebarWorkspace | 'shared';

export interface SubMenuItem {
    path: string;
    label: string;
    icon: ReactNode;
    requiredPermissions?: PermissionName[];
    requiredOriginalRoles?: UserRole[];
    workspace?: SidebarScope;
}

export interface MenuItem {
    path?: string;
    label: string;
    icon: ReactNode;
    requiredPermissions?: PermissionName[];
    requiredOriginalRoles?: UserRole[];
    workspace?: SidebarScope;
    hasSubmenu?: boolean;
    key?: string;
    children?: SubMenuItem[];
}

export interface SectionGroup {
    key: string;
    label: string;
    icon: ReactNode;
    items: MenuItem[];
    workspace: SidebarScope;
}

interface SidebarAccessContext {
    workspace: SidebarWorkspace | null;
    originalRole?: UserRole;
    hasAnyPermission: (permissions: PermissionName[]) => boolean;
}

export const resolveSidebarWorkspace = (
    effectiveRole: UserRole | undefined,
): SidebarWorkspace | null => {
    if (!effectiveRole) return null;
    return isPlatformRole(effectiveRole) ? 'platform' : 'institution';
};

const isWorkspaceVisible = (
    scope: SidebarScope | undefined,
    workspace: SidebarWorkspace | null,
): boolean => Boolean(workspace && (scope === undefined || scope === 'shared' || scope === workspace));

const hasEntryAccess = (
    entry: MenuItem | SubMenuItem,
    context: SidebarAccessContext,
): boolean => {
    const hasRequiredPermission = !entry.requiredPermissions?.length ||
        context.hasAnyPermission(entry.requiredPermissions);
    const hasRequiredOriginalRole = !entry.requiredOriginalRoles?.length ||
        Boolean(context.originalRole && entry.requiredOriginalRoles.includes(context.originalRole));

    return isWorkspaceVisible(entry.workspace, context.workspace) &&
        hasRequiredPermission &&
        hasRequiredOriginalRole;
};

export const filterSidebarSections = (
    rawSections: SectionGroup[],
    context: SidebarAccessContext,
): SectionGroup[] => rawSections
    .map(section => {
        if (!isWorkspaceVisible(section.workspace, context.workspace)) return null;

        const items = section.items
            .map(item => {
                if (!hasEntryAccess(item, context)) return null;
                if (!item.hasSubmenu || !item.children) return item;

                const children = item.children.filter(child => hasEntryAccess(child, context));
                return children.length > 0 ? { ...item, children } : null;
            })
            .filter((item): item is MenuItem => item !== null);

        return items.length > 0 ? { ...section, items } : null;
    })
    .filter((section): section is SectionGroup => section !== null);
